import React, { useState, useMemo, useCallback } from 'react';
import {
  FiChevronDown, FiChevronRight, FiPlus, FiTrash2, FiArrowUp, FiArrowDown
} from 'react-icons/fi';
import { parseLatexFields, patchLatex, insertEntryIntoSection, removeEntryFromSection } from './latexFields';

const SECTION_CONFIG = [
  { key: 'summary', label: 'Summary', type: 'text' },
  { key: 'experience', label: 'Experience', type: 'entries' },
  { key: 'projects', label: 'Projects', type: 'entries' },
  { key: 'education', label: 'Education', type: 'entries' },
  { key: 'skills', label: 'Skills', type: 'skills' },
  { key: 'certs', label: 'Certifications', type: 'entries' },
  { key: 'publications', label: 'Publications', type: 'entries' }
];

const SECTION_MARKERS = {
  summary: 'SUMMARY', experience: 'EXPERIENCE', projects: 'PROJECTS',
  education: 'EDUCATION', skills: 'SKILLS', certs: 'CERTS', publications: 'PUBLICATIONS'
};

// Extract the full text block for a section marker from the LaTeX.
const getSectionSlice = (latex, marker) => {
  const tag = `%----------${marker}----------`;
  const start = latex.indexOf(tag);
  if (start === -1) return null;
  const nextTag = latex.indexOf('%----------', start + tag.length);
  const endIdx = nextTag === -1 ? latex.indexOf('\\end{document}', start) : nextTag;
  if (endIdx === -1) return null;
  return { start, end: endIdx, text: latex.slice(start, endIdx) };
};

// Get the ordered list of section keys as they appear in the LaTeX source.
const getSectionOrder = (latex) => {
  const positions = [];
  for (const { key } of SECTION_CONFIG) {
    const marker = SECTION_MARKERS[key];
    const slice = getSectionSlice(latex, marker);
    if (slice) positions.push({ key, start: slice.start });
  }
  positions.sort((a, b) => a.start - b.start);
  return positions.map((p) => p.key);
};

// Swap two adjacent sections in the LaTeX source.
const swapSections = (latex, keyA, keyB) => {
  const markerA = SECTION_MARKERS[keyA];
  const markerB = SECTION_MARKERS[keyB];
  const sliceA = getSectionSlice(latex, markerA);
  const sliceB = getSectionSlice(latex, markerB);
  if (!sliceA || !sliceB) return latex;

  // Determine which comes first.
  const first = sliceA.start < sliceB.start ? sliceA : sliceB;
  const second = sliceA.start < sliceB.start ? sliceB : sliceA;

  return (
    latex.slice(0, first.start) +
    second.text +
    latex.slice(first.end, second.start) +
    first.text +
    latex.slice(second.end)
  );
};

function StructuredEditor({ latex, profile, selectedContent, onLatexChange }) {
  const [expanded, setExpanded] = useState({});
  const parsed = useMemo(() => parseLatexFields(latex), [latex]);
  const sectionOrder = useMemo(() => getSectionOrder(latex), [latex]);

  const toggle = useCallback((key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const updateEntry = useCallback((sectionKey, entryIndex, field, value) => {
    const fields = parseLatexFields(latex);
    const section = fields[sectionKey];
    if (!section?.entries) return;
    section.entries[entryIndex] = { ...section.entries[entryIndex], [field]: value };
    onLatexChange(patchLatex(latex, { [sectionKey]: section }));
  }, [latex, onLatexChange]);

  const updateBullet = useCallback((sectionKey, entryIndex, bulletIndex, value) => {
    const fields = parseLatexFields(latex);
    const section = fields[sectionKey];
    if (!section?.entries) return;
    const entry = { ...section.entries[entryIndex] };
    const bullets = [...entry.bullets];
    bullets[bulletIndex] = value;
    entry.bullets = bullets;
    section.entries[entryIndex] = entry;
    onLatexChange(patchLatex(latex, { [sectionKey]: section }));
  }, [latex, onLatexChange]);

  const addBullet = useCallback((sectionKey, entryIndex) => {
    const fields = parseLatexFields(latex);
    const section = fields[sectionKey];
    if (!section?.entries) return;
    const entry = { ...section.entries[entryIndex] };
    entry.bullets = [...entry.bullets, ''];
    section.entries[entryIndex] = entry;
    onLatexChange(patchLatex(latex, { [sectionKey]: section }));
  }, [latex, onLatexChange]);

  const removeBullet = useCallback((sectionKey, entryIndex, bulletIndex) => {
    const fields = parseLatexFields(latex);
    const section = fields[sectionKey];
    if (!section?.entries) return;
    const entry = { ...section.entries[entryIndex] };
    entry.bullets = entry.bullets.filter((_, i) => i !== bulletIndex);
    section.entries[entryIndex] = entry;
    onLatexChange(patchLatex(latex, { [sectionKey]: section }));
  }, [latex, onLatexChange]);

  const removeEntry = useCallback((sectionKey, entryIndex) => {
    onLatexChange(removeEntryFromSection(latex, sectionKey, entryIndex));
  }, [latex, onLatexChange]);

  const updateSummary = useCallback((value) => {
    onLatexChange(patchLatex(latex, { summary: { text: value } }));
  }, [latex, onLatexChange]);

  const updateSkill = useCallback((index, value) => {
    const fields = parseLatexFields(latex);
    if (!fields.skills?.items) return;
    const items = [...fields.skills.items];
    items[index] = value;
    onLatexChange(patchLatex(latex, { skills: { items } }));
  }, [latex, onLatexChange]);

  const removeSkill = useCallback((index) => {
    const fields = parseLatexFields(latex);
    if (!fields.skills?.items) return;
    const items = fields.skills.items.filter((_, i) => i !== index);
    onLatexChange(patchLatex(latex, { skills: { items } }));
  }, [latex, onLatexChange]);

  const addSkillToLatex = useCallback((skillName) => {
    const fields = parseLatexFields(latex);
    const items = [...(fields.skills?.items || []), skillName];
    onLatexChange(patchLatex(latex, { skills: { items } }));
  }, [latex, onLatexChange]);

  const moveSection = useCallback((key, direction) => {
    const idx = sectionOrder.indexOf(key);
    if (idx === -1) return;
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= sectionOrder.length) return;
    onLatexChange(swapSections(latex, key, sectionOrder[swapIdx]));
  }, [latex, sectionOrder, onLatexChange]);

  // Build list of profile items not in the resume.
  const availableItems = useMemo(() => {
    if (!profile) return {};
    const sel = selectedContent || {};
    const result = {};

    const buildAvailable = (key, profileArr, indexKey, labelFn) => {
      if (!profileArr?.length) return;
      const usedIndices = new Set(sel[indexKey] || []);
      const items = profileArr
        .map((item, i) => ({ index: i, item, label: labelFn(item) }))
        .filter(({ index, label }) => !usedIndices.has(index) && label);
      if (items.length) result[key] = items;
    };

    buildAvailable('experience', profile.experiences, 'experienceIndices', (e) => e.title || e.company);
    buildAvailable('projects', profile.projects, 'projectIndices', (p) => p.name);
    buildAvailable('education', profile.education, 'educationIndices', (e) => e.school || e.degree);
    buildAvailable('certs', profile.certs, 'certIndices', (c) => c.name);
    buildAvailable('publications', profile.publications, 'publicationIndices', (p) => p.title);

    return result;
  }, [profile, selectedContent]);

  // Skills from profile that aren't currently in the LaTeX.
  const availableSkills = useMemo(() => {
    if (!profile?.skills?.length) return [];
    const currentSkills = new Set((parsed.skills?.items || []).map((s) => s.toLowerCase()));
    return profile.skills.filter((s) => !currentSkills.has(s.toLowerCase()));
  }, [profile, parsed.skills]);

  const addProfileItem = useCallback((sectionKey, profileIndex) => {
    if (!profile) return;
    const sourceMap = {
      experience: profile.experiences,
      projects: profile.projects,
      education: profile.education,
      certs: profile.certs,
      publications: profile.publications
    };
    const source = sourceMap[sectionKey];
    if (!source || !source[profileIndex]) return;
    const item = source[profileIndex];

    let entry;
    if (sectionKey === 'experience') {
      const dates = [item.start, item.end].filter(Boolean).join(' -- ');
      entry = { title: item.title || '', dates, subtitle: item.company || '', detail: item.location || '', bullets: item.bullets || [] };
    } else if (sectionKey === 'projects') {
      entry = { title: item.name || '', dates: '', subtitle: 'Personal Project', detail: '', bullets: item.bullets || [] };
    } else if (sectionKey === 'education') {
      const dates = [item.start, item.end].filter(Boolean).join(' -- ');
      const degree = [item.degree, item.field].filter(Boolean).join(' in ');
      entry = { title: item.school || '', dates, subtitle: degree, detail: item.details || '', bullets: [] };
    } else if (sectionKey === 'certs') {
      entry = { title: item.name || '', dates: item.date || '', subtitle: item.issuer || '', detail: '', bullets: [] };
    } else if (sectionKey === 'publications') {
      const venue = [item.venue, item.details].filter(Boolean).join(' — ');
      const bullets = [];
      if (item.authors) bullets.push(item.authors);
      if (item.url) bullets.push(item.url);
      entry = { title: item.title || '', dates: item.date || '', subtitle: venue, detail: '', bullets };
    }

    if (!entry) return;
    onLatexChange(insertEntryIntoSection(latex, sectionKey, entry));
  }, [latex, profile, onLatexChange]);

  const renderEntry = (sectionKey, entry, entryIndex) => (
    <div className="seEntry" key={entryIndex}>
      <div className="seEntryHeader">
        <div className="seEntryFields">
          <input
            className="seInput seInputTitle"
            value={entry.title}
            onChange={(e) => updateEntry(sectionKey, entryIndex, 'title', e.target.value)}
            placeholder="Title"
          />
          <input
            className="seInput seInputSm"
            value={entry.dates}
            onChange={(e) => updateEntry(sectionKey, entryIndex, 'dates', e.target.value)}
            placeholder="Dates"
          />
          <input
            className="seInput"
            value={entry.subtitle}
            onChange={(e) => updateEntry(sectionKey, entryIndex, 'subtitle', e.target.value)}
            placeholder="Subtitle"
          />
          <input
            className="seInput seInputSm"
            value={entry.detail}
            onChange={(e) => updateEntry(sectionKey, entryIndex, 'detail', e.target.value)}
            placeholder="Detail"
          />
        </div>
        <button className="seRemoveBtn" onClick={() => removeEntry(sectionKey, entryIndex)} title="Remove entry">
          <FiTrash2 size={13} />
        </button>
      </div>
      {entry.bullets.map((bullet, bi) => (
        <div className="seBulletRow" key={bi}>
          <span className="seBulletDot" />
          <input
            className="seInput seBulletInput"
            value={bullet}
            onChange={(e) => updateBullet(sectionKey, entryIndex, bi, e.target.value)}
            placeholder="Bullet point"
          />
          <button className="seRemoveBtn seBulletRemove" onClick={() => removeBullet(sectionKey, entryIndex, bi)} title="Remove bullet">
            <FiTrash2 size={11} />
          </button>
        </div>
      ))}
      <button className="seAddBtn" onClick={() => addBullet(sectionKey, entryIndex)}>
        <FiPlus size={12} /> Add bullet
      </button>
    </div>
  );

  // Render sections in the order they appear in the LaTeX, plus any that exist
  // only as available-to-add from the profile.
  const orderedSections = useMemo(() => {
    const inLatex = sectionOrder.map((key) => SECTION_CONFIG.find((c) => c.key === key)).filter(Boolean);
    const remaining = SECTION_CONFIG.filter((c) => !sectionOrder.includes(c.key));
    return [...inLatex, ...remaining];
  }, [sectionOrder]);

  return (
    <div className="structuredEditor">
      {orderedSections.map(({ key, label, type }, sectionIdx) => {
        const section = parsed[key];
        const hasContent = type === 'text'
          ? Boolean(section?.text)
          : type === 'skills'
            ? Boolean(section?.items?.length)
            : Boolean(section?.entries?.length);
        const available = availableItems[key];
        const hasAvailableSkills = key === 'skills' && availableSkills.length > 0;
        if (!hasContent && !available && !hasAvailableSkills) return null;

        const isOpen = expanded[key] ?? false;
        const orderIdx = sectionOrder.indexOf(key);
        const canMoveUp = orderIdx > 0;
        const canMoveDown = orderIdx >= 0 && orderIdx < sectionOrder.length - 1;

        return (
          <div className="seSection" key={key}>
            <div className="seSectionHeaderRow">
              <button className="seSectionHeader" onClick={() => toggle(key)}>
                {isOpen ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                <span className="seSectionLabel">{label}</span>
                {type === 'entries' && section?.entries && (
                  <span className="seSectionCount">{section.entries.length}</span>
                )}
                {type === 'skills' && section?.items && (
                  <span className="seSectionCount">{section.items.length}</span>
                )}
              </button>
              {orderIdx >= 0 && (
                <div className="seSectionReorder">
                  <button
                    className="seReorderBtn"
                    onClick={(e) => { e.stopPropagation(); moveSection(key, -1); }}
                    disabled={!canMoveUp}
                    title="Move up"
                  >
                    <FiArrowUp size={13} />
                  </button>
                  <button
                    className="seReorderBtn"
                    onClick={(e) => { e.stopPropagation(); moveSection(key, 1); }}
                    disabled={!canMoveDown}
                    title="Move down"
                  >
                    <FiArrowDown size={13} />
                  </button>
                </div>
              )}
            </div>

            {isOpen && (
              <div className="seSectionBody">
                {type === 'text' && section && (
                  <textarea
                    className="seTextarea"
                    value={section.text || ''}
                    onChange={(e) => updateSummary(e.target.value)}
                    rows={3}
                    placeholder="Professional summary"
                  />
                )}

                {type === 'entries' && section?.entries?.map((entry, i) => renderEntry(key, entry, i))}

                {type === 'skills' && section?.items?.map((skill, i) => (
                  <div className="seSkillRow" key={i}>
                    <input
                      className="seInput seSkillInput"
                      value={skill}
                      onChange={(e) => updateSkill(i, e.target.value)}
                    />
                    <button className="seRemoveBtn" onClick={() => removeSkill(i)} title="Remove skill">
                      <FiTrash2 size={11} />
                    </button>
                  </div>
                ))}

                {type === 'skills' && availableSkills.length > 0 && (
                  <div className="seAvailableItems">
                    <span className="seAvailableLabel">Add from profile:</span>
                    {availableSkills.map((skill) => (
                      <button
                        key={skill}
                        className="seAvailableBtn"
                        onClick={() => addSkillToLatex(skill)}
                      >
                        <FiPlus size={11} /> {skill}
                      </button>
                    ))}
                  </div>
                )}

                {available && available.length > 0 && (
                  <div className="seAvailableItems">
                    <span className="seAvailableLabel">Add from profile:</span>
                    {available.map(({ index, label: itemLabel }) => (
                      <button
                        key={index}
                        className="seAvailableBtn"
                        onClick={() => addProfileItem(key, index)}
                      >
                        <FiPlus size={11} /> {itemLabel}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default StructuredEditor;
